import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { LogOut, User, Menu, X, Mail, ChevronDown, LayoutDashboard, FileText, Settings, CreditCard, Shield } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

// ScrollToTop component - scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Custom Link that scrolls to top
function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    navigate(to);
  };
  
  return (
    <a href={to} onClick={handleClick} className="text-slate-300 hover:text-white text-sm">
      {children}
    </a>
  );
}

const ADMIN_EMAILS = [
  'marc@pdfaa.co',
  'marc@gough.com.au',
  'marc@sodesign.com.au',
  'admin@pdfaa.co',
];

function isAdminEmail(email?: string | null): boolean {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
}

// User dropdown component
function UserDropdown({ user, onLogout, isAdmin }: { user: { name?: string | null; email?: string | null } | null; onLogout: () => void; isAdmin: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-slate-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
      >
        <User className="h-5 w-5" />
        <span>{user?.name || user?.email}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-slate-800 border border-slate-700 rounded-lg shadow-xl shadow-black/30 py-1 z-50">
          <a
            href="/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
            onClick={() => setIsOpen(false)}
          >
            <LayoutDashboard className="h-4 w-4 mr-3" />
            Dashboard
          </a>
          <a
            href="/dashboard/documents"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
            onClick={() => setIsOpen(false)}
          >
            <FileText className="h-4 w-4 mr-3" />
            Documents
          </a>
          <div className="border-t border-slate-700 my-1"></div>
          <a
            href="/dashboard/account"
            className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
            onClick={() => setIsOpen(false)}
          >
            <Settings className="h-4 w-4 mr-3" />
            Account Settings
          </a>
          <a
            href="/dashboard/billing"
            className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
            onClick={() => setIsOpen(false)}
          >
            <CreditCard className="h-4 w-4 mr-3" />
            Billing
          </a>
          {isAdmin && (
            <a
              href="/admin"
              className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
              onClick={() => setIsOpen(false)}
            >
              <Shield className="h-4 w-4 mr-3" />
              Admin
            </a>
          )}
          <div className="border-t border-slate-700 my-1"></div>
          <button
            onClick={() => {
              setIsOpen(false);
              onLogout();
            }}
            className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            <LogOut className="h-4 w-4 mr-3" />
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { isAuthenticated, user, logout } = useAuthStore();
  const isAdminUser = isAdminEmail(user?.email);
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Scroll to top on route change */}
      <ScrollToTop />
      
      {/* Skip link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/95 border-b border-slate-700 backdrop-blur">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src="/logo.png" 
                  alt="PDF AA Compliance - Document Remediation Service" 
                  className="h-12 w-auto"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                to="/pricing"
                className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Savings
              </Link>
              <Link
                to="/security"
                className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Security
              </Link>
              <Link
                to="/standards"
                className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Standards
              </Link>
              <Link
                to="/case-studies"
                className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Case Studies
              </Link>
              <Link
                to="/resources"
                className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Resources
              </Link>
              <Link
                to="/faq"
                className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                FAQ
              </Link>
              <Link
                to="/about"
                className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                About
              </Link>
              <Link
                to="/contact"
                className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Contact
              </Link>

              {isAuthenticated ? (
                <UserDropdown user={user} onLogout={handleLogout} isAdmin={isAdminUser} />
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="bg-white hover:bg-slate-100 text-slate-950 px-4 py-2 rounded-md text-sm font-semibold shadow-sm shadow-black/20"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-200 hover:text-white p-2"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden pt-3 pb-2">
              <div className="flex flex-col space-y-2">
                <Link
                  to="/pricing"
                  className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Savings
                </Link>
                <Link
                  to="/security"
                  className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Security
                </Link>
                <Link
                  to="/standards"
                  className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Standards
                </Link>
                <Link
                  to="/case-studies"
                  className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Case Studies
                </Link>
                <Link
                  to="/resources"
                  className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Resources
                </Link>
                <Link
                  to="/faq"
                  className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  FAQ
                </Link>
                <Link
                  to="/about"
                  className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </Link>
                <Link
                  to="/contact"
                  className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>
                {isAuthenticated ? (
                  <>
                    <div className="border-t border-slate-700 my-2 pt-2">
                      <div className="flex items-center px-3 py-2 text-gray-400 text-sm">
                        <User className="h-4 w-4 mr-2" />
                        {user?.name || user?.email}
                      </div>
                    </div>
                    <a
                      href="/dashboard"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Dashboard
                    </a>
                    <a
                      href="/dashboard/documents"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Documents
                    </a>
                    <a
                      href="/dashboard/account"
                      className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Account Settings
                    </a>
                    <a
                      href="/dashboard/billing"
                      className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Billing
                    </a>
                    {isAdminUser && (
                      <a
                        href="/admin"
                        className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Admin
                      </a>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium text-left"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="bg-white hover:bg-slate-100 text-slate-950 px-4 py-2 rounded-md text-sm font-semibold inline-block"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-24"></div>

      {/* Main content */}
      <main id="main-content" className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-700">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="PDF AA Compliance - Document Remediation Service" 
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-slate-300 text-sm">
                Making documents accessible to everyone. WCAG 2.1 AA Compliant.
              </p>
            </div>

            {/* Quick Links - Same as header navigation */}
            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <FooterLink to="/pricing">Savings</FooterLink>
                </li>
                <li>
                  <FooterLink to="/security">Security</FooterLink>
                </li>
                <li>
                  <FooterLink to="/standards">Standards</FooterLink>
                </li>
                <li>
                  <FooterLink to="/case-studies">Case Studies</FooterLink>
                </li>
                <li>
                  <FooterLink to="/resources">Resources</FooterLink>
                </li>
                <li>
                  <FooterLink to="/faq">FAQ</FooterLink>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <FooterLink to="/about">About</FooterLink>
                </li>
                <li>
                  <FooterLink to="/contact">Contact</FooterLink>
                </li>
                <li>
                  <FooterLink to="/admin">Admin</FooterLink>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <FooterLink to="/terms">Terms of Service</FooterLink>
                </li>
                <li>
                  <FooterLink to="/privacy">Privacy Policy</FooterLink>
                </li>
                <li>
                  <FooterLink to="/disclaimer">Disclaimer</FooterLink>
                </li>
              </ul>
            </div>

          </div>

          {/* Copyright */}
          <div className="mt-8 pt-8 border-t border-slate-700">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-slate-400 text-sm">
                © 2024-2026 So Design and Branding Pty Ltd. All Rights Reserved.
              </p>
              <div className="flex items-center space-x-2 text-slate-300 text-sm">
                <Mail className="h-4 w-4" />
                <a href="mailto:support@pdfaa.ai" className="hover:text-white">
                  support@pdfaa.ai
                </a>
              </div>
            </div>
            <p className="text-gray-600 text-xs text-center mt-4">
                PDF AA Compliance is a trademark of So Design and Branding Pty Ltd.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
